import {
  Chip,
  IconChevronDown,
  List,
  NaviIconSearch,
  TodayChip,
  type ListRef,
} from '@banhala/ui-kit';
import { StyleSheet } from 'react-native';
import { useRef } from 'react';
import { useNavigation, useRoute } from '@react-navigation/native';
import type { FilterListChip } from '../chipListGenerator';
import { ResetChip } from './ResetChip';
import { ThirdPlDeliveryChip } from './ThirdPlDeliveryChip';
import { DeliveryType } from '../../../../../api/enums';
import { useFilterBottomSheet } from './useFilterBottomSheet';
import { useFilterListAtom } from '../Provider/filterList/useFilterListAtom';
import { useFilterScope } from '../Provider/filterList/FilterScopeContext';
import { useLogClickFilter } from '../hooks/useLogClickFilter';
import type { AnyObject } from '../../../../../types';
import {
  useBottomSheetRoute,
  useInstanceId,
  useIsInFragmentScreens,
  useWebDragToScroll,
} from '../../../../../hooks';

type ChipsListProps = {
  filterChips: Array<FilterListChip>;
  showResetChip: boolean;
  isSearchBarOpen: boolean;
  toggleSearchBar: VoidFunction;
  logging: AnyObject;
  scrollToFilterList: VoidFunction;
  closeSearchBar: VoidFunction;
  radioQueryName?: string;
};

export const HorizontalChipList = ({
  filterChips,
  showResetChip,
  isSearchBarOpen,
  toggleSearchBar,
  closeSearchBar,
  logging,
  scrollToFilterList,
  radioQueryName,
}: ChipsListProps) => {
  // web(데스크톱) 마우스 drag-to-scroll: List(FlatList) 노드에 붙인다(터치/네이티브 no-op).
  const listRef = useRef<ListRef<FilterListChip>>(null);
  useWebDragToScroll(listRef);
  const { setToggleFilterChip } = useFilterListAtom();
  const { show: showFullScreenFilter } = useFilterBottomSheet();
  // 시트는 호출부 트리 밖에서 렌더되므로 현재 필터 스코프를 명시적으로 넘긴다.
  const filterScope = useFilterScope();
  const logClickFilter = useLogClickFilter(logging);
  const { open: openBottomSheetRoute } = useBottomSheetRoute();
  const isInFragmentScreens = useIsInFragmentScreens();
  const instanceId = useInstanceId();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const navigation = useNavigation<any>();
  const route = useRoute();

  const handleChangeFilter = (chip: FilterListChip) => {
    if (chip.type !== 'search') {
      logClickFilter({ selectedItem: chip.loggingText });
    }

    switch (chip.type) {
      case 'select': {
        if (isInFragmentScreens) {
          openBottomSheetRoute('filter', {
            filterToken: chip.filterToken,
            scrollToFilterList,
            radioQueryName,
            filterScope,
            originalInstanceId: instanceId,
            override: {
              setParams: (params: Record<string, unknown>) =>
                navigation.setParams(params),
              routeParams: (route.params ?? {}) as Record<string, unknown>,
            },
          });
        } else {
          showFullScreenFilter({
            filterToken: chip.filterToken,
            scrollToFilterList,
            radioQueryName,
            filterScope,
          });
        }
        return;
      }
      case 'search':
        toggleSearchBar();
        return;
      case 'toggle':
        scrollToFilterList();
        setToggleFilterChip({
          queryName: chip.queryName,
          queryValue: chip.queryValue,
          isSelected: chip.value,
        });
        return;
      default:
        return;
    }
  };

  const renderItem = ({
    item,
    index,
  }: {
    item: FilterListChip;
    index: number;
  }) => {
    const { text, type, value } = item;
    const isTodayChip = type === 'toggle' && text === '오늘출발';
    // 검색결과는 query_name 이 delivery_type, 카테고리관/소카테고리관은 delivery_type_str 이라
    // query_name 대신 지면 공통인 query_value 로 판별한다.
    const isThirdPlChip =
      item.type === 'toggle' && item.queryValue === DeliveryType.ThirdPL;
    if (type === 'search') {
      return (
        <Chip
          size="medium"
          state={isSearchBarOpen ? 'selected' : 'enabled'}
          leftIcon={<NaviIconSearch />}
          onPress={() => {
            handleChangeFilter(item);
          }}
        >
          {text}
        </Chip>
      );
    }
    if (type === 'select') {
      return (
        <Chip
          testID={`FILTER_LIST?LIST_INDEX=${index}&VALUE=${text}`}
          size="medium"
          state={value ? 'selected' : 'enabled'}
          rightIcon={<IconChevronDown />}
          onPress={() => {
            handleChangeFilter(item);
          }}
        >
          {text}
        </Chip>
      );
    }
    if (isThirdPlChip) {
      return (
        <ThirdPlDeliveryChip
          isSelected={value}
          onPress={() => {
            handleChangeFilter(item);
          }}
        />
      );
    }
    if (isTodayChip) {
      return (
        <TodayChip
          state={value ? 'selected' : 'enabled'}
          onPress={() => {
            handleChangeFilter(item);
          }}
        />
      );
    }
    return (
      <Chip
        size="medium"
        state={value ? 'selected' : 'enabled'}
        onPress={() => {
          handleChangeFilter(item);
        }}
      >
        {text}
      </Chip>
    );
  };

  return (
    <List
      ref={listRef}
      key={`filter-list-${filterChips.length}`}
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={style.contentContainerStyle}
      horizontal
      data={filterChips}
      ListHeaderComponent={
        showResetChip ? (
          <ResetChip
            closeSearchBar={closeSearchBar}
            logging={logging}
            scrollToFilterList={scrollToFilterList}
            radioQueryName={radioQueryName}
          />
        ) : undefined
      }
      renderItem={renderItem}
    />
  );
};

const style = StyleSheet.create({
  contentContainerStyle: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    gap: 8,
  },
});
